// QRCodeScanner.jsx (or .tsx if you use TypeScript)
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button } from '@mantine/core';
import { BrowserQRCodeReader } from '@zxing/browser';

import Aux from '../../Aux/Aux';
import Cancel from '../../../assets/icons/Cancel';
import MessageContainer from '../../utility/MessageContainer/container';
import ErrorMessage from '../../utility/errorMessages/errors';

const QRCodeScanner = (props) => {
    const [isScanning, setIsScanning] = useState(false);
    const [scanError, setScanError] = useState(false);

    const videoRef = useRef(null);
    const readerRef = useRef(null);
    const controlsRef = useRef(null);

    const toggleScanning = () => {
        if (isScanning) {
            stopCamera();
            document.body.style.overflow = '';
        } else {
            document.body.style.overflow = 'hidden';
        }
        setIsScanning(!isScanning);
    };

    const stopCamera = () => {
        // Stop ZXing scanner if running
        if (controlsRef.current && typeof controlsRef.current.stop === 'function') {
            controlsRef.current.stop();
        }
        controlsRef.current = null;

        // Stop any leftover media tracks (safety)
        const video = videoRef.current;
        const mediaStream = video && video.srcObject;
        if (mediaStream && mediaStream.getTracks) {
            mediaStream.getTracks().forEach((t) => t.stop());
        }
        if (video) video.srcObject = null;
    };

    async function startWithConstraints() {
        const video = videoRef.current;
        if (!video) return;

        // Ensure attributes for mobile
        video.setAttribute('autoplay', '');
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');

        try {
            if (!readerRef.current) {
                readerRef.current = new BrowserQRCodeReader();
            }

            // Stop any previous session
            if (controlsRef.current && typeof controlsRef.current.stop === 'function') {
                controlsRef.current.stop();
            }

            // Use constraints so the browser chooses the right camera (mobile-friendly)
            const controls = await readerRef.current.decodeFromConstraints(
                {
                    video: {
                        facingMode: { ideal: 'environment' },
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                    },
                    audio: false,
                },
                video,
                (result/*, err*/) => {
                    if (!result) return;

                    let text = result.getText();
                    let bypassUrl = false;
                    if( text?.endsWith(process.env.REACT_APP_CUSTOMER_HAMPER_BYPASS_URL) || text?.endsWith( process.env.REACT_APP_CUSTOMER_GET_STARTED_BYPASS_URL ) ) {
                        bypassUrl = true;
                    }

                    if( text?.startsWith("https://") && bypassUrl && text?.includes("theorigamicompany.com") ) {
                        text = process.env.REACT_APP_CUSTOMER_HAMPER_BYPASS_CODE;
                    }

                    if (text && text.length === 5) {
                        setScanError(false);
                        toggleScanning(); // closes overlay + stops camera
                        props?.handleScanResult?.(text);
                    }
                    else {
                        setScanError(true);
                    }
                }
            );

            controlsRef.current = controls;

            // iOS Safari sometimes needs explicit play() after metadata
            const onMeta = async () => {
                try { await video.play(); } catch {}
                video.removeEventListener('loadedmetadata', onMeta);
            };
            video.addEventListener('loadedmetadata', onMeta);
        } catch (e) {
            console.log(e);
            setScanError(true);
        }
    }

    // Start/stop scanning when overlay opens/closes
    useEffect(() => {
        if (!isScanning) return;

        let mounted = true;

        // Wait a tick so the overlay/video is in the DOM before starting
        const id = setTimeout(() => {
            if (mounted) startWithConstraints();
        }, 0);

        return () => {
            mounted = false;
            clearTimeout(id);
            stopCamera();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isScanning]);

    return (
        <Aux>
            {!isScanning && (
                <Button variant="subtle" color="gray" size="xs" onClick={toggleScanning}>
                    Scan QR Code
                </Button>
            )}

            {isScanning && (
                <Aux>
                    <CancelStyles onClick={toggleScanning}>
                        <Cancel />
                    </CancelStyles>

                    <QrStyles>
                        <section>
                            <div>
                                {/* Replaces react-qr-scanner with a plain <video> controlled by @zxing/browser */}
                                <video
                                    id="video"
                                    ref={videoRef}
                                    muted
                                    autoPlay
                                    playsInline
                                    style={{ width: '100%', maxWidth: 480, background: '#000' }}
                                />
                            </div>
                        </section>
                    </QrStyles>

                    {scanError && (
                        <ErrorContainer>
                            <MessageContainer>
                                <ErrorMessage>Please Scan your hamper&apos;s QR Code</ErrorMessage>
                            </MessageContainer>
                        </ErrorContainer>
                    )}
                </Aux>
            )}
        </Aux>
    );
};

export default QRCodeScanner;

const CancelStyles = styled.div`
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 999999999999;
    cursor: pointer;
`;

const ErrorContainer = styled.div`
    position: fixed;
    bottom: 50px;
    left: 50%;
    z-index: 999999999999;
    transform: translateX(-50%);
    width: 430px;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const QrStyles = styled.div`
    position: relative;
    z-index: 999999999;
    width: 100%;
    height: 100%;

    & section {
        position: fixed;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background-color: #212121f0;

        & div {
            padding: 0 !important;

            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);

            & video {
                position: relative !important;
                height: 100%;

                &:before {
                    content: '';
                    width: 70%;
                    height: 70%;
                    background-color: transparent;
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    border: 1px solid #ffffff;
                }
            }
        }
    }

    @media (max-width: 768px) {
        & section div {
            width: 100%;
            height: 100%;
        }
    }
`;
